import React, {
  useEffect,
  useState
} from 'react';

import {
  useParams
} from 'react-router-dom';

import {
  CheckCircle,
  FileCheck,
  Loader2,
  ShieldCheck
} from 'lucide-react';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

export default function PartnerIntake({
  mode = 'partner'
}) {
  const {
    token
  } = useParams();

  const fluxoDireto =
    mode === 'direct';

  const tokenDaRota =
    fluxoDireto
      ? 'financy'
      : token;

  const [parceiro, setParceiro] =
    useState(null);

  const [carregando, setCarregando] =
    useState(true);

  const [enviando, setEnviando] =
    useState(false);

  const [erro, setErro] =
    useState('');

  const [resultado, setResultado] =
    useState(null);

  const [duplicidade, setDuplicidade] =
    useState(null);

  const [refazer, setRefazer] =
    useState(false);

  const [encerrado, setEncerrado] =
    useState(false);

  const [dados, setDados] =
    useState({
      tipo_servico: 'cpf',
      forma_pagamento: 'cartao',
      nome: '',
      cpf: '',
      razao_social: '',
      cnpj: '',
      telefone: '',
      email: ''
    });

  const [arquivos, setArquivos] =
    useState({
      ficha_assinada: null,
      consulta_serasa: null,
      consulta_protestos: null
    });

  useEffect(() => {
    setCarregando(true);

    fetch(
      API +
      '/partner-clients/public/' +
      tokenDaRota
    )
      .then(async resposta => {
        const json =
          await resposta.json();

        if (!resposta.ok) {
          throw new Error(
            json.error ||
            'Link indisponível.'
          );
        }

        setParceiro(
          json.parceiro || {
            nome:
              'Financy Soluções Financeiras'
          }
        );
      })
      .catch(error =>
        setErro(error.message)
      )
      .finally(() =>
        setCarregando(false)
      );
  }, [tokenDaRota]);

  const alterar =
    evento => {
      setDados({
        ...dados,
        [evento.target.name]:
          evento.target.value
      });
    };

  const enviar =
    async evento => {
      evento.preventDefault();

      setEnviando(true);
      setErro('');
      setDuplicidade(null);

      try {
        const corpo =
          new FormData();

        for (
          const [chave, valor]
          of Object.entries(dados)
        ) {
          corpo.append(
            chave,
            valor
          );
        }

        corpo.append(
          'refazer',
          refazer ? 'true' : 'false'
        );

        for (
          const [chave, arquivo]
          of Object.entries(arquivos)
        ) {
          if (arquivo) {
            corpo.append(
              chave,
              arquivo
            );
          }
        }

        const resposta =
          await fetch(
            API +
            '/partner-clients/public/' +
            tokenDaRota,
            {
              method: 'POST',
              body: corpo
            }
          );

        const json =
          await resposta.json();

        if (!resposta.ok) {
          if (
            resposta.status === 409 &&
            json.code === 'DOCUMENTO_JA_CADASTRADO'
          ) {
            setDuplicidade(json);
            return;
          }

          throw new Error(
            json.error ||
            'Não foi possível concluir.'
          );
        }

        setResultado(json);
      } catch (error) {
        setErro(error.message);
      } finally {
        setEnviando(false);
      }
    };

  const pagamentos =
    dados.tipo_servico === 'cpf_cnpj'
      ? [
          [
            'cartao',
            '1️⃣ Cartão — 3x de R$ 1.530,00 sem juros (total R$ 4.590,00)'
          ],
          [
            'parcelado',
            '2️⃣ PIX parcelado — entrada de R$ 994,00 + 12x de R$ 300,00 (total R$ 4.594,00)'
          ],
          [
            'pix',
            '3️⃣ PIX à vista — R$ 3.994,00'
          ]
        ]
      : [
          [
            'cartao',
            '1️⃣ Cartão — 3x de R$ 765,00 sem juros (total R$ 2.295,00)'
          ],
          [
            'parcelado',
            '2️⃣ PIX parcelado — entrada de R$ 497,00 + 12x de R$ 150,00 (total R$ 2.297,00)'
          ],
          [
            'pix',
            '3️⃣ PIX à vista — R$ 1.997,00'
          ]
        ];

  const documentos =
    fluxoDireto
      ? [
          [
            'consulta_serasa',
            'Consulta Serasa'
          ],
          [
            'consulta_protestos',
            'Consulta de Protestos'
          ]
        ]
      : [
          [
            'ficha_assinada',
            'Ficha de inscrição assinada'
          ],
          [
            'consulta_serasa',
            'Consulta Serasa'
          ],
          [
            'consulta_protestos',
            'Consulta de Protestos'
          ]
        ];

  if (carregando) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (encerrado) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-10">
        <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 text-center shadow-2xl">
          <h1 className="text-2xl font-bold text-gray-900">
            Contratação encerrada
          </h1>
          <p className="mt-3 text-gray-600">
            Nenhum novo contrato foi criado. Quando quiser fazer outra contratação, abra este link novamente.
          </p>
        </div>
      </div>
    );
  }

  if (resultado) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-10">
        <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 text-center shadow-2xl">
          <CheckCircle
            size={58}
            className="mx-auto mb-4 text-green-500"
          />

          <h1 className="text-2xl font-bold text-gray-900">
            Cadastro concluído
          </h1>

          {fluxoDireto ? (
            <>
              <p className="mt-3 text-gray-600">
                Seus dados foram recebidos com segurança e o contrato está sendo preparado.
              </p>

              <p className="mt-5 rounded-lg bg-blue-50 p-4 text-blue-800">
                A Assinafy enviará uma mensagem em outra conversa do WhatsApp para você ler e assinar o contrato.
              </p>

              <p className="mt-3 text-gray-600">
                Depois da assinatura, o pagamento e o envio dos documentos continuarão normalmente pelo atendimento.
              </p>
            </>
          ) : (
            <>
              <p className="mt-3 text-gray-600">
                Seus dados e documentos foram encaminhados com segurança.
              </p>

              <p className="mt-5 rounded-lg bg-blue-50 p-4 text-blue-800">
                O pagamento será tratado diretamente com o parceiro responsável pelo seu atendimento.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 text-center text-white">
          <ShieldCheck
            size={44}
            className="mx-auto mb-3 text-blue-400"
          />

          <h1 className="text-3xl font-bold">
            Limpa Nome Via Judicial
          </h1>

          <p className="mt-2 text-slate-300">
            {fluxoDireto ? (
              <>
                Contratação direta com{' '}
                <strong>
                  Financy Soluções Financeiras
                </strong>
              </>
            ) : (
              <>
                Atendimento encaminhado por{' '}
                <strong>
                  {parceiro?.nome}
                </strong>
              </>
            )}
          </p>
        </div>

        {erro && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
            {erro}
          </div>
        )}

        {duplicidade && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900">
            <p className="font-semibold">
              Este CPF ou CNPJ já está cadastrado no sistema.
            </p>
            <p className="mt-2 text-sm">
              Se deseja contratar novamente para este documento, escolha Refazer. Para encerrar sem criar outro contrato, escolha Sair.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setRefazer(true);
                  setDuplicidade(null);
                  setErro('Confira a forma de pagamento e clique em Refazer contratação.');
                }}
                className="rounded-lg bg-blue-600 px-5 py-3 font-bold text-white"
              >
                Refazer
              </button>
              <button
                type="button"
                onClick={() => setEncerrado(true)}
                className="rounded-lg border border-gray-400 bg-white px-5 py-3 font-bold text-gray-700"
              >
                Sair
              </button>
            </div>
          </div>
        )}

        <form
          onSubmit={enviar}
          className="rounded-2xl bg-white p-6 shadow-2xl md:p-8"
        >
          <h2 className="mb-4 text-xl font-bold text-gray-900">
            Escolha o serviço
          </h2>

          <select
            name="tipo_servico"
            value={dados.tipo_servico}
            onChange={alterar}
            className="mb-6 w-full rounded-lg border p-3"
          >
            <option value="cpf">
              Limpa Nome CPF
            </option>

            <option value="cnpj">
              Limpa Nome CNPJ
            </option>

            <option value="cpf_cnpj">
              Limpa Nome CPF e CNPJ
            </option>
          </select>

          <div className="grid gap-4 md:grid-cols-2">
            {(dados.tipo_servico === 'cpf' ||
              dados.tipo_servico === 'cpf_cnpj') && (
              <>
                <input
                  required
                  name="nome"
                  value={dados.nome}
                  onChange={alterar}
                  placeholder="Nome completo"
                  className="rounded-lg border p-3"
                />

                <input
                  required
                  name="cpf"
                  value={dados.cpf}
                  onChange={alterar}
                  placeholder="CPF"
                  className="rounded-lg border p-3"
                />
              </>
            )}

            {(dados.tipo_servico === 'cnpj' ||
              dados.tipo_servico === 'cpf_cnpj') && (
              <>
                <input
                  required
                  name="razao_social"
                  value={dados.razao_social}
                  onChange={alterar}
                  placeholder="Razão social"
                  className="rounded-lg border p-3"
                />

                <input
                  required
                  name="cnpj"
                  value={dados.cnpj}
                  onChange={alterar}
                  placeholder="CNPJ"
                  className="rounded-lg border p-3"
                />
              </>
            )}

            <input
              required
              name="telefone"
              value={dados.telefone}
              onChange={alterar}
              placeholder="WhatsApp com DDD"
              className="rounded-lg border p-3"
            />

            <input
              required
              type="email"
              name="email"
              value={dados.email}
              onChange={alterar}
              placeholder="E-mail"
              className="rounded-lg border p-3"
            />
          </div>

          {fluxoDireto && (
            <>
              <h2 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                Forma de pagamento
              </h2>

              <div className="grid gap-3">
                {pagamentos.map(opcao => (
                  <label
                    key={opcao[0]}
                    className="flex cursor-pointer gap-3 rounded-xl border p-4"
                  >
                    <input
                      type="radio"
                      name="forma_pagamento"
                      value={opcao[0]}
                      checked={
                        dados.forma_pagamento ===
                        opcao[0]
                      }
                      onChange={alterar}
                    />

                    <span>
                      {opcao[1]}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div className="mt-8 rounded-xl bg-slate-50 p-5">
            <div className="mb-3 flex items-center gap-2">
              <FileCheck className="text-blue-600" />

              <h2 className="font-bold text-gray-900">
                Documentos opcionais
              </h2>
            </div>

            <p className="mb-4 text-sm text-gray-600">
              PDF, JPG ou PNG, com até 15 MB por arquivo.
            </p>

            {documentos.map(item => (
              <label
                key={item[0]}
                className="mb-4 block text-sm font-medium text-gray-700"
              >
                {item[1]}

                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={evento =>
                    setArquivos({
                      ...arquivos,
                      [item[0]]:
                        evento.target.files?.[0] ||
                        null
                    })
                  }
                  className="mt-1 block w-full rounded-lg border bg-white p-2"
                />
              </label>
            ))}
          </div>

          <label className="mt-6 flex items-start gap-3 text-sm text-gray-700">
            <input
              required
              type="checkbox"
              className="mt-1"
            />

            {fluxoDireto
              ? 'Confirmo que os dados são verdadeiros e autorizo seu uso para elaboração do contrato e atendimento solicitado.'
              : 'Confirmo que os dados são verdadeiros e autorizo seu envio para realização do serviço solicitado.'}
          </label>

          <button
            disabled={enviando}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-4 font-bold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {enviando && (
              <Loader2
                size={20}
                className="animate-spin"
              />
            )}

            {enviando
              ? (
                  fluxoDireto
                    ? 'Preparando contrato...'
                    : 'Enviando cadastro...'
                )
              : (
                  fluxoDireto
                    ? (
                        refazer
                          ? 'Refazer contratação'
                          : 'Continuar para o contrato'
                      )
                    : 'Enviar cadastro'
                )}
          </button>
        </form>
      </div>
    </div>
  );
}
